import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AI_COPY } from "@shared/ai-capabilities";
import { registerAcknowledgementListener, type AINotice } from "@/lib/aiReliability";

/**
 * Hosts the one-time notice shown when complex maths will be answered by
 * Standard AI. Continuing is always possible; the plans link is secondary.
 * The server records the acknowledgement, so it isn't asked again.
 */
export function AIAcknowledgementHost() {
  const [notice, setNotice] = useState<AINotice | null>(null);
  const [checked, setChecked] = useState(false);
  const resolver = useRef<((accepted: boolean) => void) | null>(null);

  useEffect(() => {
    registerAcknowledgementListener((n, resolve) => {
      resolver.current?.(false);
      resolver.current = resolve;
      setChecked(false);
      setNotice(n);
    });
    return () => registerAcknowledgementListener(null);
  }, []);

  const finish = (accepted: boolean) => {
    resolver.current?.(accepted);
    resolver.current = null;
    setNotice(null);
  };

  return (
    <Dialog open={!!notice} onOpenChange={(open) => { if (!open) finish(false); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complex maths with Standard AI</DialogTitle>
          <DialogDescription>{AI_COPY.standardExplainer}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[var(--text-primary)]">{notice?.message ?? AI_COPY.standardMathAck}</p>
        <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--border-default)] p-3 text-sm">
          <input
            type="checkbox"
            className="h-5 w-5 accent-[var(--accent-primary)]"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I understand
        </label>
        <p className="text-xs text-[var(--text-muted)]">{AI_COPY.advancedMathExplainer} It is still not guaranteed to be correct.</p>
        <DialogFooter className="gap-2 sm:gap-2">
          <a
            href="/pricing"
            className="inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            See plans with Advanced Math
          </a>
          <Button disabled={!checked} onClick={() => finish(true)}>
            Continue with Standard AI
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AIAcknowledgementHost;
